export type FloorInventoryIdsSource = {
  availableInventoryIds: number[];
  disabledInventoryIds: number[];
  forbiddenInventoryIds?: number[];
  /** Tandem-blocked ids — counted on the chip, not selectable on the plan. */
  blockedInventoryIds?: number[];
};

/**
 * Inventory ids used as the floor-plan selectable / filter-highlight pool.
 * Admins include inactive ids; forbidden ids are always excluded.
 */
export function getFloorInventoryIdsForSelection(
  floor: FloorInventoryIdsSource | null | undefined,
  canBookInactive: boolean
): number[] {
  if (!floor) {
    return [];
  }

  const forbidden = new Set(floor.forbiddenInventoryIds ?? []);
  const blocked = new Set(floor.blockedInventoryIds ?? []);
  const selectableIds = new Set<number>();

  for (const id of floor.availableInventoryIds) {
    if (!forbidden.has(id) && !blocked.has(id)) {
      selectableIds.add(id);
    }
  }

  if (canBookInactive) {
    for (const id of floor.disabledInventoryIds) {
      if (!forbidden.has(id) && !blocked.has(id)) {
        selectableIds.add(id);
      }
    }
  }

  return [...selectableIds];
}

/** Count shown on floor button and floor list — aligned for admin inactive bypass. */
export function getFloorSelectableObjectCount(
  floor: (FloorInventoryIdsSource & { numberOfBookableObjects: number }) | null | undefined,
  canBookInactive: boolean
): number {
  if (!floor) {
    return 0;
  }
  if (canBookInactive) {
    const blockedCount = floor.blockedInventoryIds?.length ?? 0;
    return getFloorInventoryIdsForSelection(floor, true).length + blockedCount;
  }
  return floor.numberOfBookableObjects;
}
